import { useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, Sun, Moon, LayoutDashboard, CalendarDays, Globe, Loader2, X, RefreshCw } from "lucide-react";
import { BRAND } from "./brand";
import { usePanel } from "./context";
import "./panel.css";

/* ---------- helpers ---------- */
export const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const pad = (n) => String(n).padStart(2, "0");
export const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => toISO(new Date());
export const prettyDate = (iso, withDow = false) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return `${withDow ? DOW[dt.getDay()] + " " : ""}${d} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
};
export const calcAge = (bd) => {
  if (!bd) return "";
  const d = new Date(bd);
  if (isNaN(d)) return "";
  const a = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return a >= 0 && a < 130 ? String(a) : "";
};
export const STATUS_LABEL = {
  pendiente: "Pendiente", confirmada: "Confirmada", reprogramar: "Pide reprogramar", cancelada: "Cancelada",
  atendida: "Atendida", previsto: "Control previsto",
};
export const LOG_LABEL = {
  enviado: "Enviado", enlace_generado: "Enlace WhatsApp", omitido: "Omitido", fallido: "Falló",
  confirmada: "Confirmó", reprogramar: "Pide reprogramar",
};

/* ---------- layout ---------- */
export function PanelPage({ children, className = "" }) {
  const { theme } = usePanel();
  return (
    <div className={`pn ${className}`} data-theme={theme} style={BRAND.cssVars}>
      {children}
    </div>
  );
}

export function BrandLogo({ className = BRAND.logoClass || "h-9" }) {
  return (
    <span className="pn-logo-pill">
      <img src={BRAND.logo} alt={BRAND.name} className={`${className} w-auto object-contain`} />
    </span>
  );
}

export function Header({ wide = false }) {
  const { user, logout, theme, toggleTheme } = usePanel();
  const nav = useNavigate();
  return (
    <header className="pn-card border-x-0 border-t-0 pn-noprint">
      <div className={`${wide ? "max-w-7xl" : "max-w-6xl"} mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-3`}>
        <Link to="/dashboard" aria-label="Ir al dashboard"><BrandLogo /></Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/dashboard" className="pn-btn px-3 py-2 pn-sub hover:pn-ink hidden md:inline-flex"><LayoutDashboard size={16} /> Dashboard</Link>
          <Link to="/dashboard?tab=agenda" className="pn-btn px-3 py-2 pn-sub hidden md:inline-flex"><CalendarDays size={16} /> Agenda</Link>
          <a href="/" className="pn-btn px-3 py-2 pn-sub hidden lg:inline-flex"><Globe size={16} /> Ver sitio</a>
          <button onClick={toggleTheme} className="pn-icon-btn" title={theme === "dark" ? "Modo claro" : "Modo oscuro"} aria-label="Cambiar tema">
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <span className="text-sm font-semibold pn-brand hidden sm:inline px-2">{user?.name}</span>
          <button onClick={() => { logout(); nav("/login"); }} className="pn-icon-btn" title="Salir" aria-label="Salir"><LogOut size={17} /></button>
        </nav>
      </div>
    </header>
  );
}

export function Protected({ children }) {
  const { user } = usePanel();
  if (user === undefined) return <PanelPage><FullLoader /></PanelPage>;
  if (user === false) {
    return (
      <PanelPage>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="pn-sub max-w-sm">No pudimos conectar con el servidor. Si el panel estuvo inactivo puede tardar hasta un minuto en despertar.</p>
          <button className="pn-btn pn-btn-primary" onClick={() => window.location.reload()}><RefreshCw size={16} /> Reintentar</button>
        </div>
      </PanelPage>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export function FullLoader({ text = "Cargando…" }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center gap-3 pn-muted">
      <Loader2 className="pn-spin" size={20} /> {text}
    </div>
  );
}

export function PageTitle({ eyebrow, title, children, sub }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-7">
      <div className="min-w-0">
        {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.25em] pn-brand mb-1.5">{eyebrow}</p>}
        <h1 className="text-3xl font-bold pn-ink leading-tight break-words">{title}</h1>
        {sub && <div className="pn-sub mt-1 text-sm">{sub}</div>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

/* ---------- form controls ---------- */
export function Field({ label, value, onChange, type = "text", placeholder, full, required, ...rest }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="pn-label">{label}{required && <span className="pn-brand"> *</span>}</label>
      <input type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder} className="pn-field" {...rest} />
    </div>
  );
}
export function Area({ label, value, onChange, rows = 3, full, placeholder }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="pn-label">{label}</label>
      <textarea value={value ?? ""} onChange={onChange} rows={rows} placeholder={placeholder} className="pn-field resize-y" />
    </div>
  );
}
export function Select({ label, value, onChange, options, full, placeholder = "Seleccionar" }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="pn-label">{label}</label>
      <select value={value ?? ""} onChange={onChange} className="pn-field">
        <option value="">{placeholder}</option>
        {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );
}
export function Section({ title, icon: Icon, children, cols = 2, aside }) {
  return (
    <section className="pn-card rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-bold pn-ink flex items-center gap-2">{Icon && <Icon size={18} className="pn-brand" />}{title}</h2>
        {aside}
      </div>
      <div className={`grid gap-4 ${cols === 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : ""}`}>{children}</div>
    </section>
  );
}

export function Modal({ title, subtitle, icon: Icon, onClose, children, footer, maxWidth = "max-w-lg" }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="pn-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`pn-modal pn-card ${maxWidth}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="flex items-start justify-between gap-3 p-5 sm:p-6 border-b pn-line">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && <div className="w-10 h-10 shrink-0 rounded-xl pn-soft-accent flex items-center justify-center"><Icon size={19} /></div>}
            <div className="min-w-0">
              <h3 className="text-lg font-bold pn-ink leading-tight">{title}</h3>
              {subtitle && <p className="text-xs pn-sub truncate">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="pn-muted hover:pn-brand p-1" aria-label="Cerrar"><X size={20} /></button>
        </div>
        <div className="p-5 sm:p-6">{children}</div>
        {footer && <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export const StatusPill = ({ status, children }) => (
  <span className={`pn-pill pn-st-${status}`}>{children || STATUS_LABEL[status] || LOG_LABEL[status] || status}</span>
);

export const Empty = ({ icon: Icon, children }) => (
  <div className="pn-card rounded-2xl p-10 text-center pn-sub">
    {Icon && <Icon size={34} className="mx-auto mb-3 pn-muted" />}
    {children}
  </div>
);

export const WhatsAppIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.27-.2-.57-.35M12.05 21.8a9.87 9.87 0 01-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 01-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.86 9.86 0 019.88 9.89c0 5.45-4.44 9.88-9.89 9.88M20.46 3.49A11.82 11.82 0 0012.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 005.69 1.45c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.17-3.49-8.42" /></svg>
);
